import { useState } from 'react';
import { Plus, Edit, Trash2, Search, Eye, EyeOff, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  useCompanyProjects,
  useCreateCompanyProject,
  useUpdateCompanyProject,
  useDeleteCompanyProject,
  useToggleCompanyProjectActive,
} from '@/hooks/api/use-company-projects';
import type { CompanyProject } from '@/api/types';

const getProjectSchema = (t: (key: string) => string) => z.object({
  title: z.object({
    en: z.string().min(1, t('common.englishRequired') || 'English is required'),
    ar: z.string().min(1, t('common.arabicRequired') || 'Arabic is required'),
  }),
  description: z.object({
    en: z.string().optional(),
    ar: z.string().optional(),
  }).optional(),
  location: z.object({
    en: z.string().optional(),
    ar: z.string().optional(),
  }).optional(),
  completed_date: z.string().optional(),
  is_active: z.boolean().default(true),
});

type ProjectFormData = z.infer<ReturnType<typeof getProjectSchema>>;

const AdminCompanyProjects = () => {
  const { t, language } = useLanguage();
  const projectSchema = getProjectSchema(t);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<CompanyProject | null>(null);
  const [deletingProject, setDeletingProject] = useState<CompanyProject | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<{ id: number; url: string }[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<number[]>([]);

  const { data: projectsData, isLoading: loading } = useCompanyProjects();
  const createMutation = useCreateCompanyProject();
  const updateMutation = useUpdateCompanyProject();
  const deleteMutation = useDeleteCompanyProject();
  const toggleActiveMutation = useToggleCompanyProjectActive();

  const projects = Array.isArray(projectsData?.data) ? projectsData.data : [];

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      title: { en: '', ar: '' },
      description: { en: '', ar: '' },
      location: { en: '', ar: '' },
      completed_date: '',
      is_active: true,
    },
  });

  const resetForm = () => {
    form.reset({
      title: { en: '', ar: '' },
      description: { en: '', ar: '' },
      location: { en: '', ar: '' },
      completed_date: '',
      is_active: true,
    });
    setSelectedFiles([]);
    setExistingImages([]);
    setImagesToDelete([]);
  };

  const handleCreateProject = async (data: ProjectFormData) => {
    const formData = new FormData();
    formData.append('title[en]', data.title.en);
    formData.append('title[ar]', data.title.ar);
    if (data.description?.en) formData.append('description[en]', data.description.en);
    if (data.description?.ar) formData.append('description[ar]', data.description.ar);
    if (data.location?.en) formData.append('location[en]', data.location.en);
    if (data.location?.ar) formData.append('location[ar]', data.location.ar);
    if (data.completed_date) formData.append('completed_date', data.completed_date);
    formData.append('is_active', data.is_active ? '1' : '0');

    selectedFiles.forEach((file) => {
      formData.append('images[]', file);
    });

    createMutation.mutate(formData, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
        resetForm();
      },
    });
  };

  const handleEditProject = async (data: ProjectFormData) => {
    if (!editingProject) return;

    const formData = new FormData();
    formData.append('title[en]', data.title.en);
    formData.append('title[ar]', data.title.ar);
    if (data.description?.en) formData.append('description[en]', data.description.en);
    if (data.description?.ar) formData.append('description[ar]', data.description.ar);
    if (data.location?.en) formData.append('location[en]', data.location.en);
    if (data.location?.ar) formData.append('location[ar]', data.location.ar);
    if (data.completed_date) formData.append('completed_date', data.completed_date);
    formData.append('is_active', data.is_active ? '1' : '0');

    selectedFiles.forEach((file) => {
      formData.append('images[]', file);
    });

    imagesToDelete.forEach((id) => {
      formData.append('delete_images[]', id.toString());
    });

    updateMutation.mutate(
      { id: editingProject.id, data: formData },
      {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setEditingProject(null);
          resetForm();
        },
      }
    );
  };

  const handleDeleteProject = () => {
    if (!deletingProject) return;
    deleteMutation.mutate(deletingProject.id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setDeletingProject(null);
      },
    });
  };

  const openEditDialog = (project: CompanyProject) => {
    setEditingProject(project);
    form.reset({
      title: project.title as { en: string; ar: string },
      description: project.description as { en?: string; ar?: string } | undefined,
      location: project.location as { en?: string; ar?: string } | undefined,
      completed_date: project.completed_date || '',
      is_active: project.is_active,
    });
    setExistingImages(project.images?.map(img => ({ id: img.id, url: img.image_url })) || []);
    setImagesToDelete([]);
    setSelectedFiles([]);
    setIsEditDialogOpen(true);
  };

  const toggleActive = (project: CompanyProject) => {
    toggleActiveMutation.mutate(project.id);
  };

  const filteredProjects = projects.filter((project: CompanyProject) => {
    const title = project.title as { en: string; ar: string };
    return (
      title.en.toLowerCase().includes(searchTerm.toLowerCase()) ||
      title.ar.includes(searchTerm)
    );
  });

  const getTitle = (project: CompanyProject) => {
    const title = project.title as { en: string; ar: string };
    return language === 'ar' ? title.ar : title.en;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString.split('T')[0] || '-';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg sm:text-xl">{t('admin.companyProjects') || 'Company Projects (Portfolio)'}</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {t('admin.companyProjectsDesc') || 'Manage your company portfolio projects'}
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setIsCreateDialogOpen(true); }} className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              {t('common.add') || 'Add Project'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('common.search') || 'Search projects...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-xs sm:text-sm"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-sm sm:text-base">{t('common.loading') || 'Loading...'}</div>
          ) : (
            <>
              {/* Mobile Cards View */}
              <div className="sm:hidden space-y-3">
                {filteredProjects.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    {t('admin.companyProjects.noProjects') || 'No projects found'}
                  </div>
                ) : (
                  filteredProjects.map((project: CompanyProject) => (
                    <Card key={project.id} className="shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm truncate mb-2">{getTitle(project)}</h3>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('common.order') || 'Order'}:</span>
                                <span>{project.order}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('common.status') || 'Status'}:</span>
                                <Badge variant={project.is_active ? 'default' : 'secondary'} className="text-xs">
                                  {project.is_active ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('common.images') || 'Images'}:</span>
                                <span className="flex items-center gap-1">
                                  <ImageIcon className="h-3 w-3" />
                                  {project.images?.length || 0}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('common.completedDate') || 'Date'}:</span>
                                <span>{formatDate(project.completed_date)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActive(project)}
                              title={project.is_active ? t('common.deactivate') : t('common.activate')}
                              className="h-8 w-8 p-0"
                            >
                              {project.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(project)} className="h-8 w-8 p-0">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setDeletingProject(project); setIsDeleteDialogOpen(true); }}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">{t('common.order') || 'Order'}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('common.title') || 'Title'}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('common.status') || 'Status'}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('common.images') || 'Images'}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('common.completedDate') || 'Completed Date'}</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">{t('common.actions') || 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjects.map((project: CompanyProject) => (
                      <TableRow key={project.id}>
                        <TableCell className="text-xs sm:text-sm">{project.order}</TableCell>
                        <TableCell className="font-medium text-xs sm:text-sm">{getTitle(project)}</TableCell>
                        <TableCell>
                          <Badge variant={project.is_active ? 'default' : 'secondary'} className="text-xs">
                            {project.is_active ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          <div className="flex items-center gap-1">
                            <ImageIcon className="h-4 w-4" />
                            {project.images?.length || 0}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">{formatDate(project.completed_date)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActive(project)}
                              title={project.is_active ? t('common.deactivate') : t('common.activate')}
                            >
                              {project.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(project)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setDeletingProject(project); setIsDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{t('portfolio.addProject') || 'Add New Project'}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">{t('portfolio.addProjectDesc') || 'Add a new project to your portfolio'}</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleCreateProject)} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="title.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.titleEn') || 'Title (English)'}</FormLabel>
                      <FormControl><Input {...field} className="text-xs sm:text-sm" /></FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="title.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.titleAr') || 'Title (Arabic)'}</FormLabel>
                      <FormControl><Input {...field} dir="rtl" className="text-xs sm:text-sm" /></FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="description.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.descriptionEn') || 'Description (English)'}</FormLabel>
                      <FormControl><Textarea {...field} className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.descriptionAr') || 'Description (Arabic)'}</FormLabel>
                      <FormControl><Textarea {...field} dir="rtl" className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="location.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.locationEn') || 'Location (English)'}</FormLabel>
                      <FormControl><Input {...field} className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="location.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.locationAr') || 'Location (Arabic)'}</FormLabel>
                      <FormControl><Input {...field} dir="rtl" className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="completed_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">{t('common.completedDate') || 'Completed Date'}</FormLabel>
                    <FormControl><Input type="date" {...field} className="text-xs sm:text-sm" /></FormControl>
                  </FormItem>
                )}
              />

              <div>
                <FormLabel className="text-xs sm:text-sm">{t('common.images') || 'Images'}</FormLabel>
                <Input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                  className="mt-2 text-xs sm:text-sm"
                />
              </div>

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3 sm:p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-xs sm:text-sm">{t('common.active') || 'Active'}</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="w-full sm:w-auto">
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button type="submit" disabled={createMutation.isPending} className="w-full sm:w-auto">
                  {createMutation.isPending ? (t('common.creating') || 'Creating...') : (t('common.create') || 'Create')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{t('portfolio.editProject') || 'Edit Project'}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">{t('portfolio.editProjectDesc') || 'Update project details'}</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleEditProject)} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="title.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.titleEn') || 'Title (English)'}</FormLabel>
                      <FormControl><Input {...field} className="text-xs sm:text-sm" /></FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="title.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.titleAr') || 'Title (Arabic)'}</FormLabel>
                      <FormControl><Input {...field} dir="rtl" className="text-xs sm:text-sm" /></FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="description.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.descriptionEn') || 'Description (English)'}</FormLabel>
                      <FormControl><Textarea {...field} className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.descriptionAr') || 'Description (Arabic)'}</FormLabel>
                      <FormControl><Textarea {...field} dir="rtl" className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="location.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.locationEn') || 'Location (English)'}</FormLabel>
                      <FormControl><Input {...field} className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="location.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('common.locationAr') || 'Location (Arabic)'}</FormLabel>
                      <FormControl><Input {...field} dir="rtl" className="text-xs sm:text-sm" /></FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="completed_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">{t('common.completedDate') || 'Completed Date'}</FormLabel>
                    <FormControl><Input type="date" {...field} className="text-xs sm:text-sm" /></FormControl>
                  </FormItem>
                )}
              />

              {existingImages.length > 0 && (
                <div>
                  <FormLabel className="text-xs sm:text-sm">{t('portfolio.existingImages') || 'Existing Images'}</FormLabel>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                    {existingImages.map((img) => (
                      <div key={img.id} className="relative group">
                        <img src={img.url} alt="" className="w-full h-16 sm:h-20 object-cover rounded" />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                          onClick={() => {
                            setExistingImages(existingImages.filter((i) => i.id !== img.id));
                            setImagesToDelete([...imagesToDelete, img.id]);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <FormLabel className="text-xs sm:text-sm">{t('portfolio.addImages') || 'Add New Images'}</FormLabel>
                <Input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                  className="mt-2 text-xs sm:text-sm"
                />
              </div>

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3 sm:p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-xs sm:text-sm">{t('common.active') || 'Active'}</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="w-full sm:w-auto">
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button type="submit" disabled={updateMutation.isPending} className="w-full sm:w-auto">
                  {updateMutation.isPending ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="w-[95vw]">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{t('common.deleteConfirm') || 'Delete Project'}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {t('portfolio.deleteWarning') || 'Are you sure you want to delete this project? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} className="w-full sm:w-auto">
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDeleteProject} disabled={deleteMutation.isPending} className="w-full sm:w-auto">
              {deleteMutation.isPending ? (t('common.deleting') || 'Deleting...') : (t('common.delete') || 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCompanyProjects;
